import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import localize from 'scaleflow/utils/localize';

function parseDataset(dataset) {
  if (!dataset || dataset.length < 2) return [];
  const [fields, ...rows] = dataset;
  const yearIdx = fields.indexOf('year');
  const periodNumberIdx = fields.indexOf('period_number');
  const valueIdx = fields.indexOf('value');
  return rows.map((row) => ({
    year: Number(row[yearIdx]),
    period_number:
      row[periodNumberIdx] != null && row[periodNumberIdx] !== ''
        ? Number(row[periodNumberIdx])
        : null,
    value: row[valueIdx] != null ? Number(row[valueIdx]) : null,
  }));
}

export default class MetricsTrendCellComponent extends Component {
  @service('byod-metrics') byodMetrics;
  @service intl;

  @tracked latestValue = null;
  @tracked previousValue = null;
  @tracked isLoading = false;

  get diff() {
    if (this.latestValue === null || this.previousValue === null) return null;
    return this.latestValue - this.previousValue;
  }

  get direction() {
    if (this.diff === null) return null;
    if (this.diff > 0) return 'up';
    if (this.diff < 0) return 'down';
    return 'flat';
  }

  get metricModel() {
    return this.args.metric?.content ?? this.args.metric;
  }

  get sentiment() {
    if (this.direction === null) return null;
    const trend = this.metricModel?.trend;
    if (trend === 'NEUTRAL' || this.direction === 'flat') return 'neutral';
    if (trend === 'UP') return this.direction === 'up' ? 'good' : 'bad';
    if (trend === 'DOWN') return this.direction === 'down' ? 'good' : 'bad';
    return 'neutral';
  }

  get formattedDiff() {
    if (this.diff === null) return null;
    const symbol = this.metricModel?.symbol;
    const abs = Math.abs(this.diff);
    let formatted;
    if (symbol === 'percentage') {
      formatted = localize(abs, '%');
    } else if (symbol === 'currency') {
      formatted = localize(abs, ',-', this.intl.t('currency.euro'));
    } else {
      formatted = localize(abs);
    }
    const sign = this.diff > 0 ? '+ ' : '- ';
    return `${sign}${formatted}`;
  }

  @action
  async loadTrend() {
    const metric = this.metricModel;
    if (!metric?.id) return;

    const level = (metric.reporting_period ?? 'month').toLowerCase();

    this.isLoading = true;
    try {
      const dataset = await this.byodMetrics.fetchAggregates(
        metric.id,
        level,
        'L',
      );

      const sorted = parseDataset(dataset).sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return (a.period_number ?? 0) - (b.period_number ?? 0);
      });

      if (sorted.length >= 2) {
        this.latestValue = sorted[sorted.length - 1].value;
        this.previousValue = sorted[sorted.length - 2].value;
      } else if (sorted.length === 1) {
        this.latestValue = sorted[0].value;
        this.previousValue = null;
      }
    } catch {
      // trend is non-critical, fail silently
    } finally {
      this.isLoading = false;
    }
  }
}
